import { useState, useEffect, useRef, useMemo } from 'react';
import { X, Search, ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '../../../shared';
import { searchUserProfiles, type UserProfile } from '../services/userProfileService';

interface EmployeeSelectProps {
  selected: UserProfile | null;
  onChange: (employee: UserProfile | null) => void;
  placeholder?: string;
  excludeEmails?: string[];
}

export default function EmployeeSelect({
  selected,
  onChange,
  placeholder = 'Search FINNies by name or email…',
  excludeEmails = [],
}: EmployeeSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState<UserProfile[] | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const excludeSet = useMemo(() => new Set(excludeEmails), [excludeEmails]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    searchUserProfiles(search, 10)
      .then(results => { if (!cancelled) setSuggestions(results); })
      .catch(() => { if (!cancelled) { setSuggestions([]); setSearchError('Failed to load employees'); } });
    return () => { cancelled = true; };
  }, [search, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const filteredSuggestions = (suggestions ?? []).filter(
    s => !excludeSet.has(s.email) && s.email !== selected?.email,
  );

  const selectUser = (profile: UserProfile) => {
    onChange(profile);
    setSearch('');
    setIsOpen(false);
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => { setSuggestions(null); setSearchError(null); setIsOpen(!isOpen); }}
        className={cn(
          'w-full flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-left transition-colors',
          'bg-white border-zinc-200 hover:border-zinc-300',
          isOpen && 'border-blue-400 ring-1 ring-blue-400/30',
        )}
      >
        {selected ? (
          <>
            {selected.photoURL ? (
              <img src={selected.photoURL} className="h-5 w-5 rounded-full shrink-0" referrerPolicy="no-referrer" alt="" />
            ) : (
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-[9px] font-bold text-white shrink-0">
                {selected.displayName.charAt(0)}
              </span>
            )}
            <span className="flex-1 text-zinc-900 font-medium truncate">{selected.displayName}</span>
            <button type="button" onClick={clear} className="text-zinc-400 hover:text-zinc-700 shrink-0">
              <X className="h-3.5 w-3.5" />
            </button>
          </>
        ) : (
          <>
            <Search className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
            <span className="flex-1 text-zinc-400">{placeholder}</span>
            <ChevronDown className={cn('h-3.5 w-3.5 text-zinc-400 transition-transform', isOpen && 'rotate-180')} />
          </>
        )}
      </button>

      {isOpen && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1.5 bg-white border border-zinc-200 rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b border-zinc-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={e => { setSearch(e.target.value); setSuggestions(null); setSearchError(null); }}
                placeholder="Search employees…"
                className="w-full pl-9 pr-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-blue-400"
              />
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {suggestions === null ? (
              <div className="flex items-center justify-center gap-2 py-6 text-zinc-500 text-xs">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Searching…
              </div>
            ) : searchError ? (
              <div className="text-center py-6 text-red-500 text-xs">{searchError}</div>
            ) : filteredSuggestions.length === 0 ? (
              <div className="text-center py-6 text-zinc-400 text-xs">
                {search ? 'No matching employees found' : 'Start typing to search…'}
              </div>
            ) : (
              filteredSuggestions.map(profile => (
                <button
                  key={profile.uid}
                  type="button"
                  onClick={() => selectUser(profile)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-50 transition-colors text-left"
                >
                  {profile.photoURL ? (
                    <img src={profile.photoURL} className="h-7 w-7 rounded-full shrink-0" referrerPolicy="no-referrer" alt="" />
                  ) : (
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-xs font-medium text-white shrink-0">
                      {profile.displayName.charAt(0)}
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-900 truncate">{profile.displayName}</div>
                    <div className="text-xs text-zinc-400 truncate">{profile.email}</div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
